/**
 * ChatFormatter - Markdown and Mermaid diagram rendering
 * GFM, breaks, escapeHtml fallback
 */

import { marked } from "marked"
import mermaid from 'mermaid/dist/mermaid.esm.mjs'
import { parseArtifacts, renderArtifact, isArtifactObject } from './artifact-renderer.js'

export class ChatFormatter {
  constructor() {
    this.mermaidCounter = 0;
    mermaid.initialize({ startOnLoad: false, theme: 'default' });
  }

  /**
   * Parse markdown to HTML
   */
  parseMarkdown(content) {
    try {
      const html = marked.parse(content, { gfm: true, breaks: true });
      // Add Bootstrap m-0 class to all p tags to remove top/bottom margin
      // Also add ai-response class for consistent styling
      return html.replace(/<p>/g, '<p class="m-0">');
    } catch (error) {
      console.error('Markdown parse error:', error);
      return this.escapeHtml(content);
    }
  }

  /**
   * Detect and render Mermaid diagrams
   */
  async detectAndRenderMermaid(container) {
    const codeBlocks = container.querySelectorAll('code.language-mermaid');
    if (codeBlocks.length === 0) return;

    for (const codeBlock of codeBlocks) {
      // Skip if already processed
      if (codeBlock.closest('.mermaid-diagram-container')) continue;

      let code = codeBlock.textContent.trim();
      if (!code) continue;

      code = this.normalizeMermaidCode(code);
      const diagramId = `mermaid-${++this.mermaidCounter}-${Date.now()}`;

      // Create container
      const container = document.createElement('div');
      container.className = 'mermaid-diagram-container bg-light border p-3 rounded my-3';
      container.setAttribute('data-mermaid-id', diagramId);

      // Replace code block
      const preElement = codeBlock.parentElement;
      if (preElement?.tagName === 'PRE') {
        preElement.replaceWith(container);
      } else {
        codeBlock.replaceWith(container);
      }

      // Render diagram
      try {
        const { svg } = await mermaid.render(diagramId, code);
        container.innerHTML = svg;
      } catch (error) {
        container.innerHTML = `<div class="alert alert-danger">Diagram error: ${error.message}</div>`;
      }
    }
  }

  /**
   * Normalize Mermaid code
   */
  normalizeMermaidCode(code) {
    let normalized = code.trim();

    // Fix pie chart syntax
    if (normalized.startsWith('pie')) {
      normalized = normalized.replace(/pie\s+title\s+"([^"]+)"/g, 'pie title $1');
    }

    // Fix graph syntax
    if (normalized.startsWith('graph')) {
      normalized = normalized.replace(/^graph\s+L\s*$/m, 'graph LR');
      normalized = normalized.replace(/^graph\s+L([^R])/m, 'graph LR$1');
    }

    // Clean up whitespace and special characters
    normalized = normalized.replace(/[\u200B-\u200D\uFEFF]/g, '');
    normalized = normalized.replace(/\r\n/g, '\n').replace(/\r/g, '\n');
    normalized = normalized.split('\n').map(line => line.trimEnd()).join('\n');
    normalized = normalized.replace(/^\n+|\n+$/g, '');

    return normalized;
  }

  /**
   * Check if buffer has an unclosed mermaid code block (```mermaid without closing ```)
   * Used during streaming to avoid rendering incomplete diagrams
   */
  hasUnclosedMermaidBlock(buffer) {
    const mermaidStart = buffer.indexOf('```mermaid');
    if (mermaidStart === -1) return false;
    const afterStart = buffer.slice(mermaidStart + '```mermaid'.length);
    return afterStart.indexOf('```') === -1;
  }

  /**
   * Check if buffer has an unclosed ```json block (streaming in progress)
   */
  hasUnclosedJsonBlock(buffer) {
    const start = buffer.indexOf('```json');
    if (start === -1) return false;
    const afterStart = buffer.slice(start + '```json'.length);
    return afterStart.indexOf('```') === -1;
  }

  /**
   * Replace closed ```json ... ``` artifact blocks with plain-text sentinel strings
   * during streaming — the real artifact is rendered on finalize.
   * Non-artifact JSON blocks (e.g. code examples) are left as-is.
   *
   * Returns { text, sentinels } where sentinels is a list of sentinel strings that
   * must be replaced with loader divs after marked.parse() runs on text.
   */
  replaceJsonBlocksWithLoaders(text) {
    const parts = text.split('```')
    let result = ''
    const sentinels = []
    for (let i = 0; i < parts.length; i++) {
      if (i % 2 === 0) {
        // Outside a fence — plain text
        result += parts[i]
      } else {
        // Inside a fence
        const block = parts[i]
        const content = block.startsWith('json') ? block.slice(4).trim() : block.trim()
        let isArtifactJson = false
        if (content.startsWith('{')) {
          try {
            const obj = JSON.parse(content)
            isArtifactJson = isArtifactObject(obj)
          } catch (e) { /* incomplete or non-JSON */ }
        }
        if (isArtifactJson) {
          const sentinel = `\x00artifact-loader-${sentinels.length}-${Date.now()}\x00`
          sentinels.push(sentinel)
          result += `\n\n${sentinel}\n\n`
        } else {
          result += '```' + block + '```'
        }
      }
    }
    return { text: result, sentinels }
  }

  /**
   * Replace sentinel strings (from replaceJsonBlocksWithLoaders) with loader div HTML.
   * Must be called on the HTML string produced by parseMarkdown() before setting innerHTML.
   */
  applyLoaderSentinels(html, sentinels) {
    const loaderHtml =
      '<div class="ai-artifact-loader d-flex align-items-center gap-2 text-muted small my-2 p-2">' +
      '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>' +
      '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.15s"></span>' +
      '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.3s"></span>' +
      '</div>'
    let result = html
    for (const sentinel of sentinels) {
      const pPattern = `<p class="m-0">${sentinel}</p>`
      if (result.includes(pPattern)) {
        result = result.split(pPattern).join(loaderHtml)
      } else {
        result = result.split(sentinel).join(loaderHtml)
      }
    }
    return result
  }

  /**
   * Format text, render all artifacts (JSON blocks + Mermaid diagrams) into container.
   * Single entry point for final AI message rendering — replaces the
   * parseMarkdown() + detectAndRenderMermaid() pair.
   *
   * Strategy: replace each artifact JSON block with a unique loader placeholder in the
   * markdown text, render markdown (loaders appear immediately in the DOM), then swap
   * each loader placeholder with the real artifact element.  This way the spinner is
   * visible while e.g. the Excalidraw iframe is loading.
   */
  async formatWithArtifacts(text, container) {
    const artifacts = parseArtifacts(text)

    // Build a map: sentinel-string → artifact.
    // We replace each raw ```json...``` block with a plain-text sentinel (no HTML tags)
    // so that marked.parse() cannot wrap it in a <p> or otherwise relocate it.
    // After rendering we do a string-replace on the resulting HTML to insert the real
    // loader div — by that point the sentinel is already inside a <p> at most, but we
    // replace the whole <p> that contains it so the block-level div ends up at the
    // correct position in the DOM.
    let cleanText = text
    const placeholders = []
    for (const artifact of artifacts) {
      const id = `artifact-ph-${placeholders.length}-${Date.now()}`
      placeholders.push({ id, artifact })
      // Use a sentinel that marked will leave verbatim as text content inside a <p>.
      cleanText = cleanText.split(artifact.raw).join(`\n\n\x00${id}\x00\n\n`)
    }

    // Render markdown — sentinels appear as plain text nodes inside <p> tags
    let html = this.parseMarkdown(cleanText.trim())

    // Replace each sentinel (which is now inside a generated <p>...</p>) with a
    // block-level loader div.  We replace the entire <p> so the result is valid HTML.
    for (const { id } of placeholders) {
      const loaderHtml =
        `<div data-artifact-placeholder="${id}" class="ai-artifact-loader d-flex align-items-center gap-2 text-muted small my-2 p-2">` +
        '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true"></span>' +
        '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.15s"></span>' +
        '<span class="spinner-grow spinner-grow-sm" role="status" aria-hidden="true" style="animation-delay:0.3s"></span>' +
        '</div>'
      // The sentinel may be the sole content of a <p class="m-0">\x00id\x00</p>.
      // Replace that whole paragraph; fall back to replacing just the sentinel text.
      const pPattern = `<p class="m-0">\x00${id}\x00</p>`
      if (html.includes(pPattern)) {
        html = html.split(pPattern).join(loaderHtml)
      } else {
        html = html.split(`\x00${id}\x00`).join(loaderHtml)
      }
    }

    container.innerHTML = html

    // Render Mermaid diagrams found in the markdown output
    await this.detectAndRenderMermaid(container)

    // Swap each placeholder with the real artifact element.
    // We yield to the browser after markdown render so the spinner is painted at least
    // one frame before being replaced — giving a visible loading state.
    await new Promise(resolve => requestAnimationFrame(resolve))

    for (const { id, artifact } of placeholders) {
      const placeholder = container.querySelector(`[data-artifact-placeholder="${id}"]`)
      const el = renderArtifact(artifact.json)
      if (placeholder && el) {
        placeholder.replaceWith(el)
      } else if (placeholder) {
        placeholder.remove()
      } else if (el) {
        container.appendChild(el)
      }
    }

    return artifacts.length > 0
  }

  /**
   * Escape HTML special characters
   */
  escapeHtml(text) {
    const div = document.createElement('div');
    div.textContent = text;
    return div.innerHTML;
  }
}
